'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useResponsiveProp } from '@/lib/device/useBreakpoint'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { StatusDot } from '@/ui/status-dot/StatusDot'
import type { ButtonProps as AriaButtonProps } from 'react-aria-components'
import { Button as AriaButton } from 'react-aria-components'
import { useIsMounted } from 'usehooks-ts'
import { buildButtonClassName } from '../button-styles/buildButtonClassName'
import type { ButtonAppearanceProps } from '../button-styles/buttonAppearance.types'
import { ButtonContent } from './_components/ButtonContent.client'

export type { ButtonAppearanceProps } from '../button-styles/buttonAppearance.types'
export { buttonSizes, buttonVariants } from '../button-styles/ButtonStyles'
export type { ButtonSize, ButtonVariant } from '../button-styles/ButtonStyles'

export type ButtonProps = ButtonAppearanceProps &
  StrictOmit<AriaButtonProps, 'children'> & {
    isLoading?: boolean
    isDisabled?: boolean
  }

/**
 * @example
 *
 * ### ⚠️ Prop `isDisabled` ⚠️
 * Unless there are alternative ways to perform the action you should avoid `isDisabled` because it
 * will not be visible by assistive devices.
 *
 * For instance in the pagination, prev/next buttons can be disabled as the user still has the option
 * to always select a page number.
 *
 * On the other hand the button to add an item to the cart should not be disabled as the user cannot
 * add the item to the cart by other means.
 * ```tsx
 * <Button variant="primary" isDisabled>title</Button>
 * ```
 * ### Minimal (responsive)
 * At a minimum, you need to provide:
 * - `variant`
 * ```tsx
 * <Button variant="primary">Button</Button>
 * ```
 *
 * ### Minimal (responsive)
 * ```tsx
 * <Button variant="primary">Primary</Button>
 * <Button variant="secondary">Secondary</Button>
 * <Button variant="tertiary">Tertiary</Button>
 * <Button variant="quaternary">Quaternary</Button>
 * <Button variant="positive">Positive</Button>
 * <Button variant="negative">Negative</Button>
 *
 * Primary on mobile, tertiary on tablet and desktop
 * <Button variant={{ mobile: 'primary', tablet: 'tertiary' }}>Responsive variant</Button>
 *
 * Primary on mobile and tablet, tertiary on desktop
 * <Button variant={{ mobile: 'primary', desktop: 'tertiary' }}>Responsive variant</Button>
 * ```
 *
 * ### Size (responsive)
 * ```tsx
 * <Button ... size="sm">Small</Button>
 * <Button ... size="md">Medium</Button>
 * <Button ... size="lg">Large</Button>
 *
 * Small on mobile, large on tablet and desktop
 * <Button ... size={{ mobile: 'sm', tablet: 'lg' }}>Responsive size</Button>
 *
 * Small on mobile and tablet, large on desktop
 * <Button ... size={{ mobile: 'sm', desktop: 'lg' }}>Responsive size</Button>
 * ```
 *
 * ### Make it round (responsive)
 * When `isRound` is `true` you MUST provide `leftIcon` AND either textual `children` (used as the
 * hidden accessible name) or an explicit `aria-label` for an icon-only button.
 * ```tsx
 * <Button ... iconLeft={<Icon.Plus />} isRound>Title</Button>
 *
 * Icon-only round button, no visible/hidden text
 * <Button ... iconLeft={<Icon.Plus />} isRound aria-label="Add" />
 *
 * Round on mobile, inline on tablet and desktop
 * <Button ... iconLeft={<Icon.Plus />} isRound={{ mobile: true, tablet: false }}>Title</Button>
 *
 * Round on mobile and tablet, inline on desktop
 * <Button ... iconLeft={<Icon.Plus />} isRound={{ mobile: true, desktop: false }}>Title</Button>
 * ```
 *
 * ### Interaction on press
 * ```tsx
 * <Button ... onPress={() => alert('pressed')}>Press me!</Button>
 * ```
 *
 * ### With loading state
 * ```tsx
 * <Button ... isLoading>Title</Button>
 * ```
 *
 * ### With icons
 * ```tsx
 * <Button ... iconLeft={<Icon.Heart />}>Title</Button>
 * <Button ... iconRight={<Icon.Plus />}>Title</Button>
 * <Button ... iconLeft={<Icon.Heart />} iconRight={<Icon.Plus />}>Title</Button>
 * ```
 *
 * ### Force it to take full parent width
 * ```tsx
 * <Button ... takesParentWidth>title</Button>
 * ```
 *
 * ### Accessibility with `aria-label`
 * When the button text is not enough to describe the action, you can use `aria-label` to provide a
 * more descriptive label.
 * ```tsx
 * <Button ...
 *   aria-label="Ajouter Perceuse BOSCH 18V au panier" // ✅ Enough context for screen reader
 * >
 *   Ajouter // ❌ Not enough descriptive
 * </Button>
 * ```
 * ### Mobile intent
 * When the button is used in the WebView of the mobile app, you can provide an intent that will be sent to the native layer instead of executing the `onPress` callback.
 * This allows you to trigger native actions that are not possible to do on the web, like opening a native modal or navigating to a native screen.
 *
 * Possible values for the `intent` prop:
 *
 * ```tsx
 * Must be memoized to avoid sending multiple times the intent on each render
 * const intent = useMemo(() => createSomeIntent(), [])
 *
 * <Button ... intent={someIntent}>title</Button>
 * ```
 */
export function Button({
  intent,
  className,
  variant,
  size = 'md',
  isRound: isRoundProp = false,
  iconLeft,
  iconRight,
  takesParentWidth,
  isLoading = false,
  isDisabled,
  onPress,
  trackingData,
  notification,
  notificationClassName,
  children,
  'aria-label': ariaLabel,
  'data-testid': dataTestId = 'button',
  isPending,
  ...ariaProps
}: ButtonProps) {
  const isMounted = useIsMounted()
  const isRound = useResponsiveProp(isRoundProp)

  const isInWebView = useIsReactNativeWebView()

  // ⚠️ Temporary: hide buttons without intent in the WebView until their specification is ready,
  // to avoid displaying TODO alerts in production
  if (isInWebView && !intent && process.env.NODE_ENV === 'production') {
    return null
  }

  const finalClassName = buildButtonClassName({
    className: twMerge(className, isPending && 'cursor-not-allowed opacity-50'),
    variant,
    size,
    isRound: isRoundProp,
    takesParentWidth,
  })

  const button = (
    <AriaButton
      {...ariaProps}
      className={finalClassName}
      isDisabled={isDisabled}
      isPending={isPending}
      aria-label={ariaLabel || (isMounted() && isRound ? children : undefined)}
      aria-busy={isLoading}
      data-loading={isLoading || undefined}
      data-testid={dataTestId}
      onPress={() => {
        if (isLoading || isPending) {
          return
        }

        handleIntent(intent, {
          whenInBrowser: onPress,
        })

        if (trackingData) {
          pushToGtmDataLayer(trackingData)
        }
      }}
    >
      <ButtonContent
        iconLeft={iconLeft}
        iconRight={iconRight}
        isLoading={isLoading}
        isRound={isRound}
      >
        {children ?? ''}
      </ButtonContent>
    </AriaButton>
  )

  if (notification) {
    return (
      <span className="relative block shrink-0">
        {button}
        <StatusDot
          variant="info"
          className={twMerge(notificationClassName, 'absolute right-[0.4375rem] top-[0.4375rem]')}
        />
      </span>
    )
  }

  return button
}
